import {
  Component,
  OnInit,
  OnDestroy,
  inject,
  DestroyRef,
} from "@angular/core";
import { Sort, SortDirection } from "@angular/material/sort";
import { PageEvent } from "@angular/material/paginator";
import { MatSnackBar } from "@angular/material/snack-bar";
import { Router } from "@angular/router";
import GridsConfig from "@app/common/grid.config";
import { Migration } from "@app/interface/interfaces";
import { PaginatorFilter } from "@app/models/grids";
import { ApiService } from "@app/services/api.service";
import {
  ExcelExportService,
  ExcelColumnDef,
} from "@app/services/excel-export.service";
import { MigrationService } from "@app/services/migration.service";
import { MigrationStore } from "@app/services/migration.store";
import { ColumnSortConfig } from "@app/components/table-grid/table-grid/table-grid.component";
import { catchError, map, of, tap, interval, firstValueFrom } from "rxjs";
import { takeUntilDestroyed } from "@angular/core/rxjs-interop";
import { UtilsService } from "@app/services/utils.service";
import { DialogService } from "@app/services/dialog.service";

@Component({
  selector: "app-migrations",
  templateUrl: "./migrations.component.html",
  styleUrl: "./migrations.component.scss",
  standalone: false,
})
export class MigrationsComponent implements OnInit, OnDestroy {
  private destroyRef = inject(DestroyRef);
  private isFetchingMigrations = false;
  private hasPendingMigrationsFetch = false;
  private isFetchingQueuedMigrations = false;
  private autoLoadPollingSubscription: any = null;
  private isInitializing = true;

  constructor(
    private migrationService: MigrationService,
    private apiService: ApiService,
    private excelExportService: ExcelExportService,
    private migrationStore: MigrationStore,
    private router: Router,
    private snackBar: MatSnackBar,
    private utilsService: UtilsService,
    private dialogService: DialogService,
  ) {}

  // Migrations table
  migrations: Migration[] = [];
  filteredMigrations: Migration[] = [];
  paginatedMigrations: Migration[] = [];
  displayedColumns = GridsConfig.Migrations.Columns;
  columnHeaders = GridsConfig.Migrations.Headers;
  migrationsSearchText: string = "";

  /** Column type configuration for type-aware sorting */
  readonly columnSortTypes: ColumnSortConfig = {
    id: "number",
    company_id: "number",
    company_name: "string",
    created_by: "string",
    created_dt_local: "date",
    started_local: "date",
    progressPercentageLabel: "number",
    status: "string",
    post_process_status: "string",
  };

  /** Current sort state */
  currentSort: Sort = { active: "id", direction: "desc" };

  get currentSortColumn(): string {
    return this.currentSort.active || "id";
  }

  get currentSortDirection(): SortDirection {
    return this.currentSort.direction;
  }

  /** Excel column definitions , exact header names per AC2 */
  readonly excelColumns: ExcelColumnDef[] = [
    { key: "id", header: "Id", type: "number", width: 10 },
    { key: "company_id", header: "Company Id", type: "number", width: 14 },
    { key: "company_name", header: "Company Name", type: "string", width: 30 },
    { key: "created_by", header: "Created By", type: "string", width: 28 },
    { key: "created_dt", header: "Created On", type: "date", width: 24 },
    {
      key: "progressPercentage",
      header: "Progress",
      type: "number",
      width: 12,
    },
    { key: "status", header: "Status", type: "string", width: 18 },
    {
      key: "post_process_status",
      header: "Post Process Status",
      type: "string",
      width: 18,
    },
  ];

  // paginator
  paginatorFilter: PaginatorFilter = new PaginatorFilter({ pageSize: 10 });
  //loading
  loading: boolean = false;
  isAutoLoadChecked: boolean = true;
  isFirstLoad: boolean = true;
  exporting: boolean = false;

  // Deleted migrations
  deletedMigrations: Migration[] = [];
  deletedMigrationsPaginated: Migration[] = [];
  deletedMigrationsColumns = GridsConfig.DeletedMigrations.Columns;
  deletedMigrationsHeaders = GridsConfig.DeletedMigrations.Headers;
  deletedPaginatorFilter: PaginatorFilter = new PaginatorFilter({
    pageSize: 5,
  });
  deletedLoading: boolean = false;
  deletedExpanded: boolean = false;

  // Queued migrations
  queuedMigrations: any[] = [];

  ngOnInit() {
    this.getMigrations();
    if (this.isAutoLoadChecked) {
      this.startAutoLoadPolling();
    }
    //this.loadQueuedMigrations();
  }

  ngOnDestroy() {
    // Cleanup polling if active
    if (this.autoLoadPollingSubscription) {
      this.autoLoadPollingSubscription.unsubscribe();
    }
    // Cleanup is handled automatically with takeUntilDestroyed operator
  }

  onAutoLoadToggled(isEnabled: boolean): void {
    if (isEnabled) {
      console.log("Auto-load enabled");
      this.startAutoLoadPolling();
    } else {
      console.log("Auto-load disabled");
      this.stopAutoLoadPolling();
    }
  }

  private startAutoLoadPolling(): void {
    if (this.autoLoadPollingSubscription) {
      return; // Already polling
    }

    console.log("Starting auto-load polling...");
    this.autoLoadPollingSubscription = interval(60000)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(() => {
        console.log("Auto-load interval triggered");
        this.getMigrations();
      });
  }

  private stopAutoLoadPolling(): void {
    if (this.autoLoadPollingSubscription) {
      console.log("Stopping auto-load polling");
      this.autoLoadPollingSubscription.unsubscribe();
      this.autoLoadPollingSubscription = null;
    }
  }

  /** Handle sort changes from table-grid */
  onSortChanged(sort: Sort): void {
    // Ignore sort changes during initialization
    // (prevents loop after initial load)
    if (this.isInitializing) {
      console.log("Ignoring sort change during initialization");
      return;
    }

    // Check if sort actually changed to prevent redundant API calls
    if (
      sort.active === this.currentSort.active &&
      sort.direction === this.currentSort.direction
    ) {
      console.log("Sort didn't change, skipping getMigrations");
      return;
    }

    console.log("Sort changed:", sort);
    this.currentSort = sort;
    this.paginatorFilter.currentPage = 0; // Reset to first page on sort change

    if (this.isFetchingMigrations) {
      this.hasPendingMigrationsFetch = true;
      return;
    }

    this.getMigrations();
  }

  onMigrationsSearchChanged(value: string): void {
    this.migrationsSearchText = value ?? "";
    this.paginatorFilter.currentPage = 0;
    this.applyMigrationsFilterAndPagination();
  }

  clearMigrationsSearch(): void {
    this.onMigrationsSearchChanged("");
  }

  /** Handle cell click events from table-grid */
  onCellClicked(event: { element: Migration; column: string }): void {
    if (event.column === "id") {
      this.showMigrationMoreDetails(event.element);
    }
  }

  executeMigrations($event: any | null) {
    this.getMigrations();
    this.loadQueuedMigrations();
  }

  getMigrations() {
    // Prevent concurrent requests
    if (this.isFetchingMigrations) {
      return;
    }

    this.isFetchingMigrations = true;
    this.changeLoadingState(true);
    const currentPage = this.paginatorFilter.currentPage + 1;
    const pageSize = this.paginatorFilter.pageSize;
    const orderBy = this.buildOrderByParam();

    this.apiService
      .getMigrationsPaginated(currentPage, pageSize, orderBy)
      .pipe(
        map((response: { data: Migration[]; total: number }) => ({
          data: response.data.map((migration) =>
            this.migrationService.processMigrationRecord(migration),
          ),
          total: response.total,
        })),
        tap((result: { data: Migration[]; total: number }) => {
          this.migrations = result.data;
          this.paginatedMigrations = result.data;
          this.paginatorFilter.count = result.total;

          this.changeLoadingState(false);
          this.isFetchingMigrations = false;

          // After initial load completes, allow sort changes to trigger fetches
          if (this.isInitializing) {
            this.isInitializing = false;
            console.log("Initialization complete, sort changes now enabled");
          }

          if (this.hasPendingMigrationsFetch) {
            this.hasPendingMigrationsFetch = false;
            this.getMigrations();
          }
        }),
        catchError((error) => {
          console.error("Error fetching migrations:", error);
          this.changeLoadingState(false);
          this.isFetchingMigrations = false;

          if (this.hasPendingMigrationsFetch) {
            this.hasPendingMigrationsFetch = false;
            this.getMigrations();
          }

          return of({ data: [], total: 0 });
        }),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe();
  }

  loadQueuedMigrations() {
    // Prevent concurrent requests
    if (this.isFetchingQueuedMigrations) {
      return;
    }

    this.isFetchingQueuedMigrations = true;
    this.apiService
      .getQueuedMigrations()
      .pipe(
        tap(() => {
          this.isFetchingQueuedMigrations = false;
        }),
        catchError((error) => {
          console.error("Error fetching queued migrations:", error);
          this.isFetchingQueuedMigrations = false;
          return of([]);
        }),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe({
        next: (data: any[]) => {
          this.queuedMigrations = data;
        },
        error: () => {
          this.queuedMigrations = [];
        },
      });
  }

  dequeueMigration(migrationId: number, companyName: string) {
    this.dialogService.openConfirmDialog({
      title: "Remove queued migration",
      message: "Are you sure you want to remove this migration from the queue?",
      additionalMessage: `\"${companyName}\" (ID: ${migrationId})`,
      confirmText: "Remove",
      cancelText: "Cancel",
      confirmTextStyle: "warning-btn",
      onConfirm: () => {
        this.apiService
          .dequeueMigration(migrationId)
          .pipe(takeUntilDestroyed(this.destroyRef))
          .subscribe({
            next: () => {
              this.loadQueuedMigrations();
            },
            error: (err: any) => {
              console.error("Failed to dequeue migration", err);
            },
          });
      },
    });
  }

  dequeueAllMigrations() {
    this.dialogService.openConfirmDialog({
      title: "Remove all queued migrations",
      message: "Are you sure you want to clear the migration queue?",
      additionalMessage: `${this.queuedMigrations.length} migration(s) will be removed.`,
      confirmText: "Clear All",
      cancelText: "Cancel",
      confirmTextStyle: "warning-btn",
      onConfirm: () => {
        this.apiService
          .dequeueAllMigrations()
          .pipe(takeUntilDestroyed(this.destroyRef))
          .subscribe({
            next: () => {
              this.loadQueuedMigrations();
            },
            error: (err: any) => {
              console.error("Failed to dequeue all migrations", err);
            },
          });
      },
    });
  }

  paginateMigrations(event: PageEvent) {
    this.paginatorFilter.currentPage = event.pageIndex;
    this.paginatorFilter.pageSize = event.pageSize;
    this.getMigrations();
  }

  showMigrationMoreDetails(migration: Migration) {
    // If status is exception, navigate to fallout report with migration ID
    if (migration.status === "exception") {
      this.router.navigate(["/metrics-tracker"], {
        queryParams: { migrationId: migration.id },
      });
    } else {
      // Otherwise, navigate to migration details page
      this.router.navigate([`/migration/${migration.id}`]);
    }
  }

  /** Export to .xlsx with typed cells, exact headers, and filename */
  async exportToExcel(): Promise<void> {
    this.exporting = true;
    try {
      // Fetch all migrations from the API
      const allMigrations = await firstValueFrom(this.apiService.getMigrations());
      
      if (!allMigrations || allMigrations.length === 0) {
        this.exporting = false;
        return;
      }

      // Export the full sorted dataset (all migrations, not just current page)
      const sortedData = this.getSortedMigrations(allMigrations);
      await this.excelExportService.exportToExcel(
        sortedData as unknown as Record<string, unknown>[],
        this.excelColumns,
      );
      this.exporting = false;
    } catch (error) {
      this.exporting = false;
      const errorRefId = `ERR-${Date.now()}`;
      console.error(`Export failed [${errorRefId}]:`, error);

      // Non-technical message with retry option and error reference ID
      const snackBarRef = this.snackBar.open(
        `Export failed. Please try again. (Ref: ${errorRefId})`,
        "Retry",
        {
          duration: 10000,
          panelClass: ["snackbar-error"],
          horizontalPosition: "center",
          verticalPosition: "bottom",
        },
      );
      snackBarRef.onAction().subscribe(() => {
        this.exportToExcel();
      });
    }
  }

  onDeletedPanelOpened() {
    if (this.deletedMigrations.length === 0) {
      this.getDeletedMigrations();
    }
  }

  getDeletedMigrations() {
    this.deletedLoading = true;
    this.apiService
      .getDeletedMigrations()
      .pipe(
        map((migrations: Migration[]) =>
          migrations.map((migration) => {
            return this.migrationService.processMigrationRecord(migration);
          }),
        ),
        tap((processedMigrations: Migration[]) => {
          this.deletedMigrations = this.utilsService.sortArrayByNumber(
            processedMigrations,
            "id",
          );
          this.applyDeletedMigrationsPagination();
          this.deletedLoading = false;
        }),
        catchError((error) => {
          this.deletedLoading = false;
          return of([]);
        }),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe();
  }

  paginateDeletedMigrations(event: PageEvent) {
    this.deletedPaginatorFilter.currentPage = event.pageIndex;
    this.deletedPaginatorFilter.pageSize = event.pageSize;
    this.applyDeletedMigrationsPagination();
  }

  // ******** PRIVATE ********
  private applyDeletedMigrationsPagination() {
    const skipRecords: number =
      this.deletedPaginatorFilter.currentPage *
      this.deletedPaginatorFilter.pageSize;
    this.deletedMigrationsPaginated = this.deletedMigrations
      .slice(skipRecords)
      .slice(0, this.deletedPaginatorFilter.pageSize);
    this.deletedPaginatorFilter.count = this.deletedMigrations.length;
  }

  private applyMigrationsPagination() {
    this.paginatedMigrations = this.getSortedMigrations(
      this.filteredMigrations,
    );
  }

  private applyMigrationsFilterAndPagination() {
    this.filteredMigrations = this.filterMigrationsByColumns(
      this.migrations,
      this.migrationsSearchText,
    );
    this.applyMigrationsPagination();
  }

  private filterMigrationsByColumns(
    migrations: Migration[],
    searchTerm: string,
  ): Migration[] {
    const term = (searchTerm || "").trim().toLowerCase();
    if (!term) {
      return [...migrations];
    }

    return migrations.filter((migration) =>
      this.displayedColumns.some((column) => {
        const value = (migration as any)[column];
        return String(value ?? "")
          .toLowerCase()
          .includes(term);
      }),
    );
  }

  /** Return migrations sorted by the current sort state */
  private getSortedMigrations(migrations: Migration[]): Migration[] {
    if (!this.currentSort.active || this.currentSort.direction === "") {
      // No active sort â€” default to Created On descending
      return [...migrations].sort(
        (a, b) =>
          new Date(b.created_dt).getTime() - new Date(a.created_dt).getTime(),
      );
    }
    const direction = this.currentSort.direction === "asc" ? 1 : -1;
    const active = this.currentSort.active;
    const sortType = this.columnSortTypes[active] || "string";

    return [...migrations].sort((a, b) => {
      let aVal: string | number;
      let bVal: string | number;

      switch (sortType) {
        case "number":
          // For progressPercentageLabel, use the underlying progressPercentage value
          if (active === "progressPercentageLabel") {
            aVal = a.progressPercentage ?? 0;
            bVal = b.progressPercentage ?? 0;
          } else {
            aVal = (a as any)[active] ?? 0;
            bVal = (b as any)[active] ?? 0;
          }
          break;
        case "date":
          // Handle date fields - look for raw date field if display field is used
          const rawDateField = active.replace("_local", "");
          aVal = (a as any)[rawDateField]
            ? new Date((a as any)[rawDateField]).getTime()
            : 0;
          bVal = (b as any)[rawDateField]
            ? new Date((b as any)[rawDateField]).getTime()
            : 0;
          break;
        case "string":
        default:
          aVal = ((a as any)[active] || "").toString().toLowerCase();
          bVal = ((b as any)[active] || "").toString().toLowerCase();
          break;
      }

      if (typeof aVal === "number" && typeof bVal === "number") {
        return (aVal - bVal) * direction;
      }
      return String(aVal).localeCompare(String(bVal)) * direction;
    });
  }

  private changeLoadingState(state: boolean) {
    this.loading = state;
    this.migrationStore.setLoading(state);
    if (this.isFirstLoad) {
      this.isFirstLoad =
        this.isFirstLoad && !this.loading ? false : this.isFirstLoad;
    }
  }

  private buildOrderByParam(): string {
    const sortColumnMap: Record<string, string> = {
      created_dt_local: "created_dt",
      started_local: "started",
      durationText: "duration",
      progressPercentageLabel: "progress",
    };
    const column = sortColumnMap[this.currentSort.active] || this.currentSort.active;
    const direction = this.currentSort.direction;
    if (!column || !direction) {
      return "id DESC"; // sort cleared — reset to default
    }
    return `${column} ${direction === "asc" ? "ASC" : "DESC"}`;
  }
}
